'use client';

import { useAuth } from '@/hooks/useAuth';
import { useCalendarStore } from '@/stores/calendar-store';
import { uiFb } from '@/lib/i18n';
import { useBookmarkStore } from '@/stores/bookmark-store';

interface BookmarkButtonProps {
  pageSlug: string;
  pageTitle: string;
  eventDateStart?: string;
  eventCountry?: string;
  compact?: boolean;
}

export function BookmarkButton({ pageSlug, pageTitle, eventDateStart, eventCountry, compact }: BookmarkButtonProps) {
  const { user, signIn } = useAuth();
  const lang = useCalendarStore((s) => s.lang);
  const isBookmarked = useBookmarkStore((s) => s.isBookmarked(pageSlug));
  const toggleBookmark = useBookmarkStore((s) => s.toggleBookmark);

  const handleClick = () => {
    if (!user) {
      signIn();
      return;
    }
    toggleBookmark(user.id, {
      event_slug: pageSlug,
      event_title: pageTitle,
      event_date_start: eventDateStart,
      event_country: eventCountry,
    });
  };

  if (compact) {
    return (
      <button
        onClick={handleClick}
        className="rounded px-1.5 py-0.5 text-caption transition-colors hover:bg-surface-hover"
        aria-label={isBookmarked ? uiFb('ui.remove_bookmark', 'Remove bookmark', lang) : uiFb('ui.bookmark_event', 'Bookmark event', lang)}
        aria-pressed={isBookmarked}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill={isBookmarked ? 'var(--semantic-error)' : 'none'}
          stroke={isBookmarked ? 'var(--semantic-error)' : 'currentColor'}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="inline-block"
        >
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-label font-medium transition-colors ${
        isBookmarked
          ? 'bg-semantic-error/10 text-semantic-error'
          : 'text-muted hover:bg-surface-hover hover:text-text-secondary'
      }`}
      aria-label={isBookmarked ? uiFb('ui.remove_bookmark', 'Remove bookmark', lang) : uiFb('ui.bookmark_event', 'Bookmark event', lang)}
      aria-pressed={isBookmarked}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill={isBookmarked ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
      {isBookmarked ? uiFb('ui.saved', 'Saved', lang) : uiFb('ui.save', 'Save', lang)}
    </button>
  );
}
